// Starting redwoodjs v6.0.0
// In this file, all Page components from 'src/pages` are auto-imported. Nested
// directories are supported, and should be uppercase. Each subdirectory will be
// prepended onto the component name.
// @todo remove import and use the auto-imported components
// Examples:
//
// 'src/pages/HomePage/HomePage.js'         -> HomePage
// 'src/pages/Admin/BooksPage/BooksPage.js' -> AdminBooksPage

import React, { useEffect } from 'react'

import { Font } from '@react-pdf/renderer'

import { Router, Route, Set, PrivateSet } from '@redwoodjs/router'

import { useAuth } from './auth'
import AdminLayout from './layouts/AdminLayout/AdminLayout'
import ShopLayout from './layouts/ShopLayout/ShopLayout'
import TwoFactorWrapper from './components/TwoFactorWrapper/TwoFactorWrapper'

const Routes: React.FC = () => {
  useEffect(() => {
    try {
      Font.register({
        family: 'Open Sans',
        fonts: [
          {
            src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf',
          },
          {
            src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf',
            fontWeight: 700,
          },
        ],
      })
    } catch (error) {
      console.error('Error registering fonts', error)
    }
  }, [])

  return (
    <Router useAuth={useAuth}>
      <Route path="/two-factor" page={TwoFactorPage} name="twoFactor" />
      <PrivateSet unauthenticated="login" roles="admin" wrap={[AdminLayout, TwoFactorWrapper]}>
        <Route path="/admin/welcome" page={AdminAdminWelcomePage} name="adminWelcome" />
        <Route path="/admin/assortments/new" page={AdminAssortmentNewAssortmentPage} name="newAssortment" />
        <Route path="/admin/assortments/{id}/edit" page={AdminAssortmentEditAssortmentPage} name="editAssortment" />
        <Route path="/admin/assortments" page={AdminAssortmentAssortmentsPage} name="assortments" />
        <Route path="/admin/vendors/new" page={AdminVendorNewVendorPage} name="newVendor" />
        <Route path="/admin/vendors/{id}/edit" page={AdminVendorEditVendorPage} name="editVendor" />
        <Route path="/admin/vendors" page={AdminVendorVendorsPage} name="vendors" />
        <Route path="/admin/products/new" page={AdminProductNewProductPage} name="newProduct" />
        <Route path="/admin/products/{id}/edit" page={AdminProductEditProductPage} name="editProduct" />
        <Route path="/admin/products" page={AdminProductProductsPage} name="products" />
        <Route path="/admin/categories/new" page={AdminCategoryNewCategoryPage} name="newCategory" />
        <Route path="/admin/categories/{id}/edit" page={AdminCategoryEditCategoryPage} name="editCategory" />
        <Route path="/admin/categories" page={AdminCategoryCategoriesPage} name="categories" />
        <Route path="/admin/orders/new" page={AdminOrderNewOrderPage} name="newOrder" />
        <Route path="/admin/orders/{id}/edit" page={AdminOrderEditOrderPage} name="editOrder" />
        <Route path="/admin/orders" page={AdminOrderOrdersPage} name="orders" />
        <Route path="/admin/teams/kickstart/{id}" page={AdminTeamKickstartPage} name="kickstart" />
        <Route path="/admin/teams/new" page={AdminTeamNewTeamPage} name="newTeam" />
        <Route path="/admin/teams/{id}/edit" page={AdminTeamEditTeamPage} name="editTeam" />
        <Route path="/admin/teams" page={AdminTeamTeamsPage} name="teams" />
        <Route path="/admin/users/new" page={AdminUserNewUserPage} name="newUser" />
        <Route path="/admin/users/{id}/edit" page={AdminUserEditUserPage} name="editUser" />
        <Route path="/admin/users" page={AdminUserUsersPage} name="users" />
        <Route path="/admin/change-password" page={MemberChangePasswordPage} name="adminChangePassword" />
        <Route path="/admin/reports" page={AdminReportsPage} name="reports" />
        <Route path="/admin/sales-period-report" page={AdminSalesPeriodReportPage} name="salesPeriodReport" />
        <Route path="/admin/sales-period/multiple-handling" page={ManageSalesPage} name="manageSales" />
        <Route path="/admin/selling-period/{id}" page={SellingPeriodPage} name="sellingPeriod" />
        <Route path="/admin/statistics" page={StatisticsPage} name="statistics" />
        <Route path="/admin/intresseanmalningar" page={AdminInquiryInquiryPage} name="inquiry" />
        <Route path="/admin/intresseanmalningar/{id}" page={AdminInquiryEditInquiryPage} name="editInquiry" />
        <Route path="/admin/campaigns/new" page={AdminCampaignCodeNewCampaignPage} name="newCampaign" />
        <Route path="/admin/campaigns/{id}/edit" page={AdminCampaignCodeEditCampaignPage} name="editCampaign" />
        <Route path="/admin/campaigns" page={AdminCampaignCodeCampaignPage} name="campaigns" />
        <Route path="/admin/transactions" page={AdminTransactionPage} name="transactions" />
        <Route path="/admin/clubs" page={AdminClubClubsPage} name="clubs" />
        <Route path="/admin/clubs/new" page={AdminClubNewClubPage} name="newClub" />
        <Route path="/admin/clubs/{id}/edit" page={AdminClubEditClubPage} name="editClub" />
      </PrivateSet>
      <PrivateSet unauthenticated="login" roles="lead" wrap={AdminLayout}>
        <Route path="/my-team" page={LeadMyTeamWelcomePage} name="myTeamWelcomePage" />
        <Route path="/my-team/users/new" page={LeadMyTeamNewUserOnMyTeamPage} name="newUserOnMyTeam" />
        <Route path="/my-team/users/{id}/edit" page={LeadMyTeamEditUsersOnTeamsPage} name="editUserOnMyTeam" />
        <Route path="/my-team/users" page={LeadMyTeamUsersOnMyTeamPage} name="usersOnMyTeam" />
        <Route path="/my-team/settings" page={LeadTeamSettingsPage} name="teamSettings" />
        <Route path="/my-team/change-password" page={MemberChangePasswordPage} name="changePassword" />
        <Route path="/my-team/statistics/member/{username}/{sellingPeriodId}" page={LeadMyTeamTeamStatisticsMemberPage} name="teamStatisticsMember" />
        <Route path="/my-team/statistics" page={LeadMyTeamTeamStatisticsPage} name="teamStatistics" />
        <Route path="/my-team/selling-information" page={LeadMyTeamTeamleadSellingInformationPage} name="teamleadSellingInformation" />
        <Route path="/my-team/my-statistics" page={LeadMyTeamTeamleadStatisticsPage} name="teamleadStatistics" />
        <Route path="/my-team/delivery-info" page={LeadMyTeamTeamDeliveryPage} name="teamDelivery" />
        <Route path="/my-team/bonus" page={MemberMemberMilestonesPage} name="teamMilestones" />
        <Route path="/my-team/goals" page={MemberMemberGoalsPage} name="teamGoals" />
        <Route path="/my-team/toplist" page={MemberMemberToplistPage} name="teamToplist" />
        <Route path="/my-team/sociala-medier" page={LeadMyTeamTeamLeadSocialMediaPage} name="teamLeadSocialaMedier" />
      </PrivateSet>

      <PrivateSet unauthenticated="login" roles="member" wrap={AdminLayout}>
        <Route path="/member/statistics" page={MemberMemberStatisticsPage} name="memberStatistics" />
        <Route path="/member/welcome" page={MemberMemberWelcomePage} name="memberWelcome" />
        <Route path="/member/selling-information" page={MemberSellingInformationPage} name="sellingInformation" />
        <Route path="/member/salj-tips" page={MemberMemberSellingTipsPage} name="sellingTips" />
        <Route path="/member/settings" page={MemberMemberSettingsPage} name="memberSettings" />
        <Route path="/member/delivery" page={MemberMemberDeliveryPage} name="memberDelivery" />
        <Route path="/member/bonus" page={MemberMemberMilestonesPage} name="memberMilestones" />
        <Route path="/member/goals" page={MemberMemberGoalsPage} name="memberGoals" />
        <Route path="/member/toplist" page={MemberMemberToplistPage} name="memberToplist" />
        <Route path="/member/sociala-medier" page={MemberMemberSocialMediaPage} name="memberSocialMedia" />
      </PrivateSet>

      <PrivateSet unauthenticated="login" roles="club_supervisor" wrap={AdminLayout}>
        <Route path="/my-club/welcome" page={SupervisorSupervisorWelcomePage} name="supervisorWelcome" />
        <Route path="/my-club/users" page={SupervisorClubUsersPage} name="supervisorClubUsers" />
        <Route path="/my-club/statistics" page={SupervisorSupervisorStatsPage} name="supervisorStatistics" />
        <Route path="/my-club/statistics/member/{username}/{sellingPeriodId}" page={SupervisorSupervisorStatsMemberPage} name="supervisorStatisticsMember" />
        <Route path="/my-club/leaderboard" page={SupervisorSupervisorToplistPage} name="supervisorToplist" />
      </PrivateSet>

      <Set wrap={ShopLayout}>
        <Route path="/login" page={ShopLoginPage} name="login" />
        <Route path="/auto-login" page={AutoLoginPage} name="autoLogin" />
        <Route path="/forgot-password" page={ShopForgotPasswordPage} name="forgotPassword" />
        <Route path="/reset-password" page={ShopResetPasswordPage} name="resetPassword" />
        <Route path="/registrering/{id}" page={RegistrationPage} name="registration" />
        <Route path="/uppstart/{id}" page={UppstartPage} name="uppstart" />
        <Route path="/registrera/{id}" page={ClubRegistreraPage} name="clubRegistration" />
        <Route notfound page={NotFoundPage} />
      </Set>
    </Router>
  )
}

export default Routes
